import { useState } from 'react';
import { TeamState } from '@/types';

interface FirstServerTeamSelectionProps {
  isOpen: boolean;
  teamA: TeamState;
  teamB: TeamState;
  setNumber: number;
  onSelectTeam: (teamId: string) => void;
  onCancel?: () => void;
}

export default function FirstServerTeamSelection({
  isOpen,
  teamA,
  teamB,
  setNumber,
  onSelectTeam,
  onCancel,
}: FirstServerTeamSelectionProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleContinue = () => {
    if (selectedTeamId) {
      onSelectTeam(selectedTeamId);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <div className="text-center mb-6">
          <h2 className="text-lg font-bold text-text-primary mb-1">Select First Serving Team</h2>
          <p className="text-xs text-text-muted">Set {setNumber}</p>
        </div>

        <p className="text-sm text-text-secondary text-center mb-4">
          Which team will serve first in this set?
        </p>

        <div className="space-y-2 mb-6">
          <button
            onClick={() => setSelectedTeamId(teamA.id)}
            className={`w-full py-4 px-4 rounded-xl font-bold text-sm transition-all flex items-center gap-3 ${
              selectedTeamId === teamA.id
                ? 'ring-2 ring-white/30'
                : 'border border-border hover:border-team-a'
            }`}
            style={{
              backgroundColor: selectedTeamId === teamA.id ? teamA.color : 'transparent',
              borderColor: selectedTeamId !== teamA.id ? teamA.color : undefined,
              color: selectedTeamId === teamA.id ? 'white' : teamA.color,
            }}
          >
            {teamA.logoUrl ? (
              <img src={teamA.logoUrl} alt={teamA.name} className="w-8 h-8 rounded object-contain bg-white/10 p-0.5" />
            ) : (
              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: teamA.color }}>
                <span className="text-white text-xs font-bold">{teamA.shortName?.charAt(0) || '?'}</span>
              </div>
            )}
            <span className="flex-1 text-left">{teamA.name}</span>
            {selectedTeamId === teamA.id && <span className="text-white/80">●</span>}
          </button>

          <button
            onClick={() => setSelectedTeamId(teamB.id)}
            className={`w-full py-4 px-4 rounded-xl font-bold text-sm transition-all flex items-center gap-3 ${
              selectedTeamId === teamB.id
                ? 'ring-2 ring-white/30'
                : 'border border-border hover:border-team-b'
            }`}
            style={{
              backgroundColor: selectedTeamId === teamB.id ? teamB.color : 'transparent',
              borderColor: selectedTeamId !== teamB.id ? teamB.color : undefined,
              color: selectedTeamId === teamB.id ? 'white' : teamB.color,
            }}
          >
            {teamB.logoUrl ? (
              <img src={teamB.logoUrl} alt={teamB.name} className="w-8 h-8 rounded object-contain bg-white/10 p-0.5" />
            ) : (
              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: teamB.color }}>
                <span className="text-white text-xs font-bold">{teamB.shortName?.charAt(0) || '?'}</span>
              </div>
            )}
            <span className="flex-1 text-left">{teamB.name}</span>
            {selectedTeamId === teamB.id && <span className="text-white/80">●</span>}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {onCancel && (
            <button
              onClick={onCancel}
              className="py-3 px-4 rounded-lg font-semibold text-sm bg-surface-raised text-text-primary border border-border transition-all hover:bg-surface"
            >
              CANCEL
            </button>
          )}
          <button
            onClick={handleContinue}
            disabled={!selectedTeamId}
            className={`${onCancel ? '' : 'w-full'} py-3 px-4 rounded-lg font-bold text-sm bg-primary text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            CONTINUE
          </button>
        </div>
      </div>
    </div>
  );
}
